import type { LearningStatus, Worksheet, WorksheetAssignee } from '@mathflat/domain/Worksheet'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { useState } from 'react'
import { generatePath, useHistory, useParams } from 'react-router'

import { Badge } from '@/component/atoms/Badge'
import Button from '@/component/atoms/Button'
import { Icon } from '@/component/atoms/Icon'
import { Loader } from '@/component/atoms/Loader'
import { qrScanStore } from '@/contentComponents/QrScoring/qrScanStore'
import { RouteName } from '@/router'

import type { GradeGroup } from './groupAssigneesByGrade'
import { useMobileStudentSelectQuery } from './mobileStudentSelect.query'
import S from './MobileStudentSelect.style'

const STATUS_BADGE = {
  INCOMPLETE: {
    text: '미채점',
    color: 'var(--Colors-Gray-700)',
    backgroundColor: 'var(--Colors-Gray-100)',
  },
  PROGRESS: {
    text: '채점 중',
    color: 'var(--Colors-Sky-400)',
    backgroundColor: 'var(--Colors-Sky-50)',
  },
  COMPLETE: {
    text: '채점 완료',
    color: 'var(--Colors-Green-600)',
    backgroundColor: 'var(--Colors-Green-50)',
  },
  EXPIRED: {
    text: '만료',
    color: 'var(--Colors-Gray-700)',
    backgroundColor: 'var(--Colors-Gray-100)',
  },
} as const satisfies Record<
  LearningStatus,
  { text: string; color: string; backgroundColor: string }
>

const StatusBadge = ({ status }: { status: LearningStatus }) => {
  const { text, color, backgroundColor } = STATUS_BADGE[status]
  return (
    <Badge size="m" radius="100" color={color} backgroundColor={backgroundColor}>
      {text}
    </Badge>
  )
}

const GradeSection = ({
  group,
  onPick,
}: {
  group: GradeGroup
  onPick: (assignee: WorksheetAssignee) => void
}) => {
  const [open, setOpen] = useState(true)
  return (
    <section>
      <button
        type="button"
        css={S.gradeHead}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <Icon
          name={open ? 'dropdown_arrow_down' : 'dropdown_arrow_right'}
          size={20}
          color="var(--Colors-Gray-500)"
        />
        <span>{group.label}</span>
        <span css={S.gradeCount}>{group.assignees.length}명</span>
      </button>
      {open &&
        group.assignees.map((assignee) => (
          <button
            key={assignee.studentWorksheetId}
            type="button"
            css={S.row}
            onClick={() => onPick(assignee)}
          >
            <span css={S.name}>{assignee.studentName}</span>
            <StatusBadge status={assignee.status} />
            <Icon name="chevron_right" size={20} color="var(--Colors-Gray-500)" />
          </button>
        ))}
    </section>
  )
}

/**
 * 학습지 머리와 학년별 학생 목록. 휴대폰 학생 선택 화면과 태블릿·PC 의 QR 진입 화면이 같은 내용을 보여 준다.
 * 학생을 누른 뒤 열 채점 화면만 쓰는 쪽이 고른다.
 */
export const StudentSelectContent = ({
  worksheetId,
  onPick,
}: {
  worksheetId: Worksheet['id']
  onPick: (assignee: WorksheetAssignee) => void
}) => {
  const { data, isPending, isError, refetch } = useMobileStudentSelectQuery(worksheetId)
  const total = data?.groups.reduce((sum, group) => sum + group.assignees.length, 0) ?? 0

  return (
    <>
      {data && (
        <div css={S.worksheetHead}>
          <b css={S.worksheetTitle}>{data.title}</b>
          <span css={S.sub}>
            {total === 0
              ? `${tJosa('출제')}받은 학생 0명`
              : `${tJosa('출제')}받은 학생 ${total}명 · 학생을 고르면 채점 화면이 열려요`}
          </span>
        </div>
      )}
      <main css={S.scroll}>
        {isPending ? (
          <Loader />
        ) : isError ? (
          <div css={S.notice}>
            <b css={S.noticeTitle}>학생 목록을 불러오지 못했어요</b>
            <button type="button" css={S.retry} onClick={() => refetch()}>
              다시 시도
            </button>
          </div>
        ) : total === 0 ? (
          <div css={S.notice}>
            <b css={S.noticeTitle}>{`이 학습지를 ${tJosa('출제')}받은 학생이 없어요`}</b>
            <span
              css={S.sub}
            >{`[수업 준비]에서 ${tJosa('출제')}한 뒤 QR로 채점할 수 있어요.`}</span>
          </div>
        ) : (
          data.groups.map((group) => <GradeSection key={group.key} group={group} onPick={onPick} />)
        )}
      </main>
    </>
  )
}

const MobileStudentSelect = () => {
  const history = useHistory()
  const params = useParams<{ worksheetId: string }>()
  const worksheetId = Number(params.worksheetId) as Worksheet['id']

  return (
    <div css={S.page}>
      <header css={S.top}>
        <button
          type="button"
          css={S.back}
          onClick={() => history.replace(RouteName.mobileHome)}
          aria-label="뒤로"
        >
          <Icon name="chevron_left" size={24} color="var(--Colors-Gray-800)" />
        </button>
        <b css={S.topTitle}>학생 선택</b>
      </header>
      <StudentSelectContent
        worksheetId={worksheetId}
        onPick={(assignee) =>
          history.push(
            generatePath(RouteName.mobileGroupScoring, {
              worksheetId,
              studentWorksheetId: assignee.studentWorksheetId,
            }),
          )
        }
      />
      {qrScanStore.canScan && (
        <footer css={S.nextBar}>
          <Button size="l" width="full" leftIcon="qr_scan" onClick={qrScanStore.start}>
            다음 QR 찍기
          </Button>
        </footer>
      )}
    </div>
  )
}

export default MobileStudentSelect
